import type { HubConnection, IRetryPolicy } from "@microsoft/signalr";
import type { ILogger } from "@crialume/sdk";
import { RealtimeEmitter, type ManagedRealtimeClient } from "./realtime-emitter";

/** O que o cliente usa da `HubConnection` (permite trocar por um dublê nos testes). */
export interface HubConnectionLike {
  readonly state: string;
  start(): Promise<void>;
  stop(): Promise<void>;
  on(method: string, handler: (...args: unknown[]) => void): void;
  onreconnecting(callback: (error?: Error) => void): void;
  onreconnected(callback: (connectionId?: string) => void): void;
  onclose(callback: (error?: Error) => void): void;
}

export interface HubConnectionFactoryOptions {
  url: string;
  accessTokenFactory: () => Promise<string>;
  retryPolicy: IRetryPolicy;
}

export interface SignalRRealtimeClientOptions {
  /** URL do hub (ex.: "/hubs/app" ou "https://api.exemplo.com/hubs/app"). */
  url: string;
  /** Lido a cada (re)conexão: o servidor fecha a conexão quando o token expira. */
  getAccessToken: () => string | null | Promise<string | null>;
  logger?: ILogger;
  /**
   * Espera entre tentativas (ms), por número da tentativa; a última se repete
   * para sempre. Padrão: 0, 2s, 5s, 10s, 30s.
   */
  retryDelaysMs?: readonly number[];
  /** Substitui a criação da conexão (testes). */
  createConnection?: (options: HubConnectionFactoryOptions) => HubConnectionLike;
}

const DEFAULT_RETRY_DELAYS_MS = [0, 2_000, 5_000, 10_000, 30_000] as const;

/**
 * Enviado pelo servidor depois de colocar a conexão nos grupos dela. O `start()` termina no handshake, antes disso: até o
 * evento chegar, eventos para a conexão podem se perder. Só então o status vira `connected` (e os módulos recarregam).
 */
export const READY_EVENT = "connection.ready";

/** Servidor antigo, que não envia `connection.ready`: depois disto, a conexão é dada como pronta mesmo assim. */
const READY_FALLBACK_MS = 5_000;

/** `HubConnectionState.Disconnected`, sem importar o SignalR só pelo valor. */
const DISCONNECTED = "Disconnected";

/**
 * Conexão que só baixa o SignalR no primeiro `start()`: telas que nunca abrem o tempo real
 * (a landing, ou o app com `realtime.enabled` desligado) não carregam a biblioteca.
 * Os handlers registrados antes disso são aplicados quando a conexão é criada.
 */
function createHubConnection(options: HubConnectionFactoryOptions): HubConnectionLike {
  const pending: ((hub: HubConnection) => void)[] = [];
  let hub: HubConnection | null = null;
  let loading: Promise<HubConnection> | null = null;

  const load = () =>
    (loading ??= import("@microsoft/signalr").then(
      ({ HubConnectionBuilder, LogLevel }) => {
        hub = new HubConnectionBuilder()
          .withUrl(options.url, { accessTokenFactory: options.accessTokenFactory })
          .withAutomaticReconnect(options.retryPolicy)
          // Falhas viram status + log próprio; sem o ruído do console do SignalR.
          .configureLogging(LogLevel.None)
          .build();
        pending.splice(0).forEach((apply) => apply(hub!));
        return hub;
      },
      (error: unknown) => {
        // Chunk indisponível (rede, deploy novo): a próxima tentativa baixa de novo.
        loading = null;
        throw error;
      },
    ));
  const apply = (action: (hub: HubConnection) => void) => {
    if (hub) action(hub);
    else pending.push(action);
  };

  return {
    get state() {
      return hub?.state ?? DISCONNECTED;
    },
    start: async () => (await load()).start(),
    stop: async () => {
      await hub?.stop();
    },
    on: (method, handler) => apply((connection) => connection.on(method, handler)),
    onreconnecting: (callback) => apply((connection) => connection.onreconnecting(callback)),
    onreconnected: (callback) => apply((connection) => connection.onreconnected(callback)),
    onclose: (callback) => apply((connection) => connection.onclose(callback)),
  };
}

/**
 * Cliente SignalR do hub único da aplicação (`/hubs/app`, só servidor → tela).
 *
 * - Token via query string (`access_token`), lido a cada (re)conexão;
 * - reconexão automática SEM limite de tentativas (o padrão do SignalR desiste
 *   após 4), inclusive quando a primeira conexão falha ou o servidor fecha
 *   a conexão (token expirado);
 * - nunca lança: hub fora do ar só deixa o status fora de `connected`;
 * - `connected` só depois de `connection.ready` (o servidor já colocou a conexão nos grupos).
 */
export function createSignalRRealtimeClient(
  options: SignalRRealtimeClientOptions,
): ManagedRealtimeClient {
  return new SignalRRealtimeClient(options);
}

class SignalRRealtimeClient extends RealtimeEmitter implements ManagedRealtimeClient {
  private readonly connection: HubConnectionLike;
  private readonly registered = new Set<string>();
  private readonly delays: readonly number[];
  /** O kernel quer a conexão aberta (há sessão). */
  private wanted = false;
  private attempt = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private connecting: Promise<void> | null = null;
  /** `connection.ready` chegou desde o último (re)início da conexão. */
  private ready = false;
  /** O transporte está aberto e espera o `connection.ready`. */
  private awaitingReady = false;
  private readyTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly options: SignalRRealtimeClientOptions) {
    super(options.logger, (event) => this.register(event));
    this.delays = options.retryDelaysMs?.length ? options.retryDelaysMs : DEFAULT_RETRY_DELAYS_MS;

    const factory = options.createConnection ?? createHubConnection;
    this.connection = factory({
      url: options.url,
      accessTokenFactory: async () => (await options.getAccessToken()) ?? "",
      retryPolicy: { nextRetryDelayInMilliseconds: (ctx) => this.delay(ctx.previousRetryCount) },
    });

    // O evento pode chegar antes de o start() terminar: guarda e aplica quando o transporte abrir.
    this.connection.on(READY_EVENT, () => {
      this.ready = true;
      if (this.awaitingReady) this.markReady();
    });
    this.connection.onreconnecting((error) => {
      this.logger?.warn("tempo real: conexão perdida, reconectando", error?.message);
      this.resetReady();
      this.setStatus("reconnecting");
    });
    this.connection.onreconnected(() => {
      this.logger?.info("tempo real: reconectado");
      this.waitForReady();
    });
    this.connection.onclose((error) => {
      this.resetReady();
      if (!this.wanted) {
        this.setStatus("disconnected");
        return;
      }
      // Fechado pelo servidor (ex.: token expirado) ou reconexão encerrada: tenta de novo.
      this.logger?.warn("tempo real: conexão fechada, tentando de novo", error?.message);
      this.setStatus("reconnecting");
      this.scheduleRetry();
    });

    for (const event of this.subscribedEvents()) this.register(event);
  }

  start = (): Promise<void> => {
    this.wanted = true;
    if (this.connecting) return this.connecting;
    if (this.connection.state !== DISCONNECTED) return Promise.resolve();
    this.clearRetry();
    return this.connect();
  };

  stop = async (): Promise<void> => {
    this.wanted = false;
    this.clearRetry();
    this.resetReady();
    this.attempt = 0;
    try {
      await this.connection.stop();
    } catch (error) {
      this.logger?.debug("tempo real: falha ao encerrar a conexão", error);
    }
    this.setStatus("disconnected");
  };

  private connect(): Promise<void> {
    this.setStatus(this.getStatus() === "reconnecting" ? "reconnecting" : "connecting");
    this.connecting = (async () => {
      try {
        this.ready = false;
        await this.connection.start();
        this.attempt = 0;
        if (this.wanted) this.waitForReady();
        else await this.stop();
      } catch (error) {
        if (!this.wanted) {
          this.setStatus("disconnected");
          return;
        }
        this.logger?.warn(
          "tempo real: hub indisponível, nova tentativa em breve",
          error instanceof Error ? error.message : error,
        );
        this.scheduleRetry();
      } finally {
        this.connecting = null;
      }
    })();
    return this.connecting;
  }

  /** Transporte aberto: conectado quando o `connection.ready` chegar (ou já chegou), com tempo limite de segurança. */
  private waitForReady(): void {
    this.awaitingReady = true;
    if (this.ready) {
      this.markReady();
      return;
    }
    this.clearReadyTimer();
    this.readyTimer = setTimeout(() => {
      this.logger?.warn(
        "tempo real: o servidor não enviou connection.ready; considerando a conexão pronta",
      );
      this.markReady();
    }, READY_FALLBACK_MS);
  }

  private markReady(): void {
    this.clearReadyTimer();
    this.awaitingReady = false;
    if (this.wanted) this.setStatus("connected");
  }

  private resetReady(): void {
    this.ready = false;
    this.awaitingReady = false;
    this.clearReadyTimer();
  }

  private clearReadyTimer(): void {
    if (this.readyTimer) clearTimeout(this.readyTimer);
    this.readyTimer = null;
  }

  private scheduleRetry(): void {
    this.clearRetry();
    const wait = this.delay(this.attempt++);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (this.wanted && this.connection.state === DISCONNECTED) {
        void this.connect();
      }
    }, wait);
  }

  private clearRetry(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private delay(attempt: number): number {
    return this.delays[Math.min(attempt, this.delays.length - 1)] ?? 30_000;
  }

  /** Um handler do SignalR por evento; o despacho para os inscritos é nosso. */
  private register(event: string): void {
    // Chamado pelo construtor da base antes de `connection` existir: o laço
    // no fim do construtor registra os pendentes.
    if (!this.connection || this.registered.has(event)) return;
    this.registered.add(event);
    this.connection.on(event, (payload) => this.dispatch(event, payload));
  }
}
